import { useCallback, useEffect, useState } from 'react';
import { Building2, ListChecks, Plus, Power, PowerOff } from 'lucide-react';
import { api, ErrorApi } from '../api';
import EstadoVacio from '../ui/EstadoVacio';
import Modal from '../ui/Modal';
import Ayuda from '../ui/Ayuda';

// =====================================================================
// UNA REGLA ESCRITA UNA VEZ QUE VALE PARA LAS CINCO · Escala Fase E3
// =====================================================================
// Con cinco razones sociales, la misma regla había que escribirla cinco
// veces. Basta olvidar UNA para que esa empresa se quede sin control, y
// no sale ningún error: ahí simplemente no se pide firma, y nadie lo
// nota hasta que alguien mira.
//
// LA PANTALLA DICE EL NOMBRE DEL GRUPO Y NO CUÁNTAS EMPRESAS SON, y la
// diferencia tiene historia: contarlas obligaba a saltarse el
// aislamiento entre empresas para adornar una cifra. «Vale para todas
// las empresas de tu grupo Comercial Ríos» es igual de honesto y no abre
// nada. El caso entero está en la migración 365.

// LO QUE TEGO SABE VIGILAR. Se pide al servidor con las reglas, para que
// esta lista no se quede vieja el día que se añada un tipo.
const UNIDADES = {
  DINERO:     { campo: 'valor_minimo',      etiqueta: 'A partir de cuántos pesos', sufijo: 'pesos' },
  CANTIDAD:   { campo: 'cantidad_minima',   etiqueta: 'A partir de cuántas piezas', sufijo: 'piezas' },
  PORCENTAJE: { campo: 'porcentaje_minimo', etiqueta: 'A partir de qué porcentaje', sufijo: '%' },
  NINGUNA:    null,
};

function condicionLegible(regla, unidad) {
  const u = UNIDADES[unidad];
  if (!u || !regla.condicion) return 'Siempre, sin importar el importe';
  const v = regla.condicion[u.campo];
  if (v === undefined || v === null) return 'Siempre, sin importar el importe';
  return `Desde ${v} ${u.sufijo}`;
}

export default function ReglasDelGrupo({ irA }) {
  const [grupo, setGrupo] = useState(null);
  const [reglas, setReglas] = useState([]);
  const [tipos, setTipos] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState('');
  const [nueva, setNueva] = useState(null);
  const [confirmar, setConfirmar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      // LAS REGLAS DE LA EMPRESA VIAJAN TAMBIÉN, y no sobran: de ahí
      // salen los tipos que se pueden vigilar y el permiso que cada uno
      // pide para aprobar. Sin eso, el formulario tendría que traer una
      // lista escrita a mano que envejece sola.
      const [g, propias] = await Promise.all([
        api.get('/escala/reglas-de-grupo'),
        api.get('/autorizaciones/reglas/todas'),
      ]);
      setGrupo(g);
      setReglas(g.reglas || []);
      setTipos(propias || []);
      setError('');
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo cargar la lista. Revisa tu conexión y vuelve a entrar.');
    } finally {
      setCargando(false);
    }
  }, []);

  useEffect(() => { cargar(); }, [cargar]);

  async function cambiarEncendido(regla) {
    try {
      await api.put(`/escala/reglas-de-grupo/${regla.id}`,
        { activa: !regla.activa });
      setConfirmar(null);
      await cargar();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo guardar el cambio. Vuelve a intentarlo.');
    }
  }

  if (cargando) return <p className="p-6 text-gray-500">Cargando las reglas del grupo…</p>;

  // SIN GRUPO NO SE PINTA UNA TABLA VACÍA: se dice qué es esto y por qué
  // está vacío, que es lo que pide un estado vacío útil.
  if (grupo && !grupo.grupo) {
    return (
      <div className="p-6">
        <Cabecera nombreGrupo={null} />
        <EstadoVacio
          icono={Building2}
          titulo="Tu empresa no pertenece a ningún grupo"
          descripcion={'Esta pantalla sirve para poner una misma regla en varias '
            + 'razones sociales a la vez. Las reglas de tu empresa se ponen en '
            + 'Administración → Reglas de autorización.'}
          /* EX-5 (1 oct 2026): la frase decía «Autorizaciones → Reglas», una
             pantalla que no existía. Ahora nombra la de verdad y lleva a ella. */
          accion={irA && (
            <button
              type="button"
              onClick={() => irA('accesos-reglas-de-autorizacion')}
              className="inline-flex min-h-11 items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-semibold"
            >
              <ListChecks className="h-4 w-4" aria-hidden="true" /> Abrir Reglas de autorización
            </button>
          )}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <Cabecera nombreGrupo={grupo?.nombre_grupo} />

      {error && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}

      <button
        type="button"
        onClick={() => setNueva({})}
        className="inline-flex items-center gap-2 rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold"
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        Escribir una regla del grupo
      </button>

      {reglas.length === 0 ? (
        <EstadoVacio
          icono={Building2}
          titulo="Tu grupo todavía no tiene ninguna regla"
          descripcion={'Cada empresa sigue usando las suyas. Escribe una aquí y '
            + 'valdrá para todas, sin tener que repetirla en cada una.'}
        />
      ) : (
        <ul className="space-y-3">
          {reglas.map((r) => {
            const tipo = tipos.find((t) => t.tipo_accion === r.tipo_accion);
            const propia = tipo?.activa;
            return (
              <li key={r.id} className="rounded-2xl border p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">
                      {tipo?.nombre_accion || r.tipo_accion}
                    </div>
                    <div className="text-sm text-gray-600">
                      {condicionLegible(r, tipo?.unidad)}
                      {' · '}
                      {r.aprobadores_requeridos === 1
                        ? 'una firma' : `${r.aprobadores_requeridos} firmas`}
                      {' · '}
                      {r.plazo_horas} horas para decidir
                    </div>
                  </div>
                  <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-semibold ${
                    r.activa ? 'bg-green-50 text-green-700 border border-green-200'
                             : 'bg-gray-100 text-gray-600 border border-gray-200'}`}
                  >
                    {r.activa ? 'Encendida' : 'Apagada'}
                  </span>
                </div>

                <p className="text-sm text-gray-700">{r.motivo}</p>

                {/* CUÁNTA GENTE PUEDE FIRMARLA, ANTES DE ENCENDERLA.
                    Si solo hay una persona con el permiso, en cuanto esa
                    persona pida algo su solicitud se queda sin nadie que
                    la resuelva — porque nadie firma lo suyo — y el
                    trabajo se para sin que nadie entienda por qué.

                    DICE «EN TU EMPRESA» Y NO «EN EL GRUPO», y eso no es
                    una disculpa: contar la gente de las otras exigiría
                    cruzar el inquilino, que es justo lo que este carril
                    promete no hacer. Un número que dijera «en el grupo
                    hay 7» sin poder demostrarlo sería peor que no
                    darlo. */}
                {r.se_queda_sin_quien_firme && (
                  <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                    En tu empresa hay {r.aprobadores_aqui} persona(s) con
                    permiso para autorizar esto, y hacen falta al menos{' '}
                    {r.aprobadores_requeridos + 1}: quien pide no puede
                    firmar lo suyo. Si la enciendes así, aquí las solicitudes
                    se quedarán sin nadie que las resuelva. En las otras
                    empresas del grupo puede ser distinto: eso se mira desde
                    cada una.
                  </p>
                )}

                {/* LA REGLA DE LA EMPRESA GANA, Y SE DICE AQUÍ —pegado a
                    la regla de la que se habla— y no en una nota arriba
                    del todo. Quien mira esta línea es quien necesita
                    saberlo. */}
                {propia && (
                  <Ayuda id="escala.reglas_grupo.la_empresa_gana" />
                )}

                <button
                  type="button"
                  onClick={() => setConfirmar(r)}
                  className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold"
                >
                  {r.activa
                    ? <><PowerOff className="h-4 w-4" aria-hidden="true" /> Apagar</>
                    : <><Power className="h-4 w-4" aria-hidden="true" /> Encender</>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* TODA ACCIÓN RIESGOSA PIDE CONFIRMACIÓN EXPLICANDO LA
          CONSECUENCIA EN UNA FRASE. Y aquí la consecuencia es ancha de
          verdad: cae sobre todas las razones sociales del grupo. */}
      {confirmar && (
        <Modal
          titulo={confirmar.activa ? 'Apagar esta regla' : 'Encender esta regla'}
          onCerrar={() => setConfirmar(null)}
        >
          <p className="text-sm leading-relaxed">
            {confirmar.activa
              ? `Desde ahora, ninguna empresa del grupo ${grupo?.nombre_grupo} pedirá `
                + 'firma para esto. Lo que ya se pidió sigue su curso.'
              : `Desde ahora, TODAS las empresas del grupo ${grupo?.nombre_grupo} `
                + 'pedirán firma para esto, salvo las que tengan su propia regla.'}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              onClick={() => cambiarEncendido(confirmar)}
              className="rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold"
            >
              {confirmar.activa ? 'Sí, apagarla' : 'Sí, encenderla'}
            </button>
            <button
              type="button"
              onClick={() => setConfirmar(null)}
              className="rounded-xl border px-4 py-2.5 font-semibold"
            >
              Mejor no
            </button>
          </div>
        </Modal>
      )}

      {nueva && (
        <FormularioRegla
          tipos={tipos}
          onCerrar={() => setNueva(null)}
          onGuardada={async () => { setNueva(null); await cargar(); }}
        />
      )}
    </div>
  );
}

function Cabecera({ nombreGrupo }) {
  return (
    <header className="space-y-1">
      <h1 className="text-xl font-bold">Reglas para todas las empresas del grupo</h1>
      {/* EL SUBTITULO NOMBRA A ESTE GRUPO y por eso no es una ayuda:
          una ayuda es un texto fijo con versión, y el nombre del grupo
          cambia con quien mira. La ayuda de al lado explica qué es esta
          pantalla; el subtítulo dice de quién son estas reglas. */}
      {nombreGrupo && (
        <p className="text-sm text-gray-600">
          Las de tu grupo {nombreGrupo}.
        </p>
      )}
      <Ayuda id="escala.reglas_grupo.pantalla" />
    </header>
  );
}

function FormularioRegla({ tipos, onCerrar, onGuardada }) {
  const [tipoAccion, setTipoAccion] = useState('');
  const [umbral, setUmbral] = useState('');
  const [firmas, setFirmas] = useState(1);
  const [horas, setHoras] = useState(48);
  const [motivo, setMotivo] = useState('');
  const [error, setError] = useState('');
  const [guardando, setGuardando] = useState(false);

  const tipo = tipos.find((t) => t.tipo_accion === tipoAccion);
  const unidad = UNIDADES[tipo?.unidad];

  // BOTÓN DESHABILITADO CON LA EXPLICACIÓN VISIBLE, nunca gris a secas.
  const falta = !tipoAccion
    ? 'Elige primero qué se vigila.'
    : motivo.trim().length < 8
      ? 'Escribe por qué pones esta regla: dentro de un año, quien la lea '
        + 'necesita saber si sigue haciendo falta.'
      : '';

  async function guardar() {
    setGuardando(true);
    try {
      const condicion = unidad && umbral !== ''
        ? { [unidad.campo]: Number(umbral) } : null;
      await api.post('/escala/reglas-de-grupo', {
        tipo_accion: tipoAccion,
        permiso_para_aprobar: tipo.permiso_para_aprobar,
        condicion,
        aprobadores_requeridos: Number(firmas),
        plazo_horas: Number(horas),
        motivo: motivo.trim(),
      });
      await onGuardada();
    } catch (e) {
      setError(e instanceof ErrorApi ? e.message
        : 'No se pudo guardar. Vuelve a intentarlo.');
      setGuardando(false);
    }
  }

  return (
    <Modal titulo="Escribir una regla del grupo" onCerrar={onCerrar}>
      <div className="space-y-4">
        <label className="block">
          <span className="text-sm font-semibold">Qué se vigila</span>
          <select
            value={tipoAccion}
            onChange={(e) => setTipoAccion(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
          >
            <option value="">Elige una…</option>
            {tipos.map((t) => (
              <option key={t.tipo_accion} value={t.tipo_accion}>
                {t.nombre_accion}
              </option>
            ))}
          </select>
          {tipo && (
            <span className="block text-xs text-gray-600 mt-1 leading-relaxed">
              {tipo.descripcion}
            </span>
          )}
        </label>

        {unidad && (
          <label className="block">
            <span className="text-sm font-semibold">{unidad.etiqueta}</span>
            <input
              type="number"
              inputMode="decimal"
              value={umbral}
              onChange={(e) => setUmbral(e.target.value)}
              className="mt-1 w-full rounded-xl border px-3 py-2.5"
              placeholder="Déjalo vacío para pedir firma siempre"
            />
            <Ayuda id="escala.reglas_grupo.umbral" />
          </label>
        )}

        <label className="block">
          <span className="text-sm font-semibold">Cuántas firmas hacen falta</span>
          <select
            value={firmas}
            onChange={(e) => setFirmas(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
          >
            <option value={1}>Una</option>
            <option value={2}>Dos</option>
            <option value={3}>Tres</option>
          </select>
          <Ayuda id="escala.reglas_grupo.firmas" />
        </label>

        <label className="block">
          <span className="text-sm font-semibold">Horas para decidir</span>
          <input
            type="number"
            value={horas}
            min={1}
            max={720}
            onChange={(e) => setHoras(e.target.value)}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
          />
          <Ayuda id="escala.reglas_grupo.plazo" />
        </label>

        <label className="block">
          <span className="text-sm font-semibold">Por qué pones esta regla</span>
          <textarea
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-xl border px-3 py-2.5"
            placeholder="Ejemplo: todo descuento grande lo revisa la matriz."
          />
        </label>

        <Ayuda id="escala.reglas_grupo.nace_apagada" />

        {error && (
          <div className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={guardar}
            disabled={!!falta || guardando}
            className="rounded-xl bg-[var(--color-marca)] px-4 py-2.5 text-white font-semibold disabled:opacity-50"
          >
            {guardando ? 'Guardando…' : 'Guardar apagada'}
          </button>
          <button type="button" onClick={onCerrar} className="rounded-xl border px-4 py-2.5 font-semibold">
            Cancelar
          </button>
          {falta && <span className="text-xs text-gray-600 basis-full">{falta}</span>}
        </div>
      </div>
    </Modal>
  );
}
